import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Wrench } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [allowed, setAllowed] = useState(null); // null = checking

  useEffect(() => {
    api.get("/auth/registration-status")
      .then((r) => {
        if (!r.data?.enabled) {
          toast.error("New accounts are invite-only. Contact the administrator.");
          navigate("/login", { replace: true });
        } else {
          setAllowed(true);
        }
      })
      .catch(() => setAllowed(true)); // if the check fails, fall back to normal flow
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(email, password, name);
      toast.success("Account created");
      navigate("/", { replace: true });
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (allowed === null) return null;  // still checking registration status

  return (
    <div className="min-h-screen w-full flex items-center justify-center p-6 bg-slate-50">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-md bg-blue-700 flex items-center justify-center text-white">
            <Wrench className="w-5 h-5" />
          </div>
          <div>
            <div className="font-heading font-extrabold text-xl text-slate-900">FixTrack</div>
            <div className="text-xs text-slate-500 uppercase tracking-wider">Create Account</div>
          </div>
        </div>
        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="font-heading text-2xl">Create your workspace</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Your name</Label>
                <Input id="name" value={name} onChange={(e) => setName(e.target.value)} required data-testid="register-name" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="register-email" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password (min 6)</Label>
                <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} data-testid="register-password" />
              </div>
              {error && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="register-error">
                  {error}
                </div>
              )}
              <Button type="submit" disabled={loading} className="w-full bg-blue-700 hover:bg-blue-800" data-testid="register-submit">
                {loading ? "Creating…" : "Create account"}
              </Button>
              <div className="text-xs text-slate-500 text-center">
                Already have one?{" "}
                <Link to="/login" className="text-blue-700 font-medium" data-testid="goto-login">Sign in</Link>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
